/* globals PROJECT_3_MIXPANEL_TOKEN, ROLLBAR_TOKEN */
import pick from 'lodash/pick';
import {initMixpanel, initRollbar} from './tracking-setup';

const rollbar = initRollbar(ROLLBAR_TOKEN);

interface TrackingOptions {
  includeReportProperties?: boolean;
  includeReportPrefix?: boolean;
}

/**
 * Set of utility methods to capture and process all
 * report specific event tracking
 */
export default class ReportTracker {
  public appName: string;
  public appPersistentProperties?: Function;
  public isInstanceProject: boolean;
  public productionEvents?: Set<string>;
  public productionPrefix: string;
  public context: any;
  public mixpanel: any;
  public rollbar: any;

  constructor({
    context,
    appName,
    appPersistentProperties = () => {
      /* noop */
    },
    appToken,
    productionEvents = new Set<string>(),
    productionPrefix,
  }) {
    this.isInstanceProject = !!appToken;
    this.mixpanel = initMixpanel(PROJECT_3_MIXPANEL_TOKEN);
    if (this.isInstanceProject) {
      this.mixpanel = initMixpanel(appToken, appName, {}, this.mixpanel);
    }
    this.appName = appName;
    this.appPersistentProperties = appPersistentProperties;
    this.productionEvents = productionEvents;
    this.productionPrefix = productionPrefix;
    this.context = context;
    this.rollbar = rollbar;

    this.identifyUser();
    this.setupRollbar();
  }

  /**
   * Handles track events generated by components.
   * Each component triggering this event is responsible
   * for providing an eventName and props to track
   */
  public onAppTrackEvent(ev) {
    const {eventName, props} = ev.detail;
    ev.stopPropagation();
    this.track(eventName, props || {});
  }

  /**
   * Tracks an event and props on the app's mixpanel project
   * and checks if the event will be sent to the Project 3
   * mixpanel project with an optional prefix
   */
  public track(event: string, properties: object, trackingOptions?: TrackingOptions) {
    const user = this.getUserData();
    const options = {
      includeReportProperties: true,
      includeReportPrefix: true,
      ...trackingOptions,
    };

    properties = this.populateEventProperties(properties, options.includeReportProperties);
    if (this.isInstanceProject) {
      this.mixpanel[this.appName].track(event, {
        ...properties,
        'User ID': user.userID,
      });
    }

    // Send bubbled `track` events to project 3 from components and widgets
    // defining their own event prefix(mp-table, mp-query-builder, etc...)
    // TODO (pablofierro): consolidate all track events to use `util/dom#emitTrackEvent`
    // to figure out when an event has a prefix instead of inspecting the event name
    const isPrefixedEvent = event.startsWith(`[`);

    if (this.productionEvents.has(event) || isPrefixedEvent) {
      const eventName =
        !isPrefixedEvent && this.productionPrefix && options.includeReportPrefix
          ? `[${this.productionPrefix}] ${event}`
          : event;

      this.mixpanel.track(eventName, properties);
    }
  }

  /**
   * Registers super properties for all projects
   */
  public register(properties) {
    if (this.isInstanceProject) {
      this.registerProject(properties);
    }
    this.mixpanel.register(properties);
  }

  /**
   * Registers super properties for instance project
   */
  public registerProject(properties) {
    this.mixpanel[this.appName].register(properties);
  }

  /**
   * Sets people properties for the instance project
   */
  public setProjectPeopleProperties(properties) {
    this.mixpanel[this.appName].people.set(properties);
  }

  /**
   * Sets people properties all mixpanel projects
   */
  public setPeopleProperties(properties) {
    if (this.isInstanceProject) {
      this.setProjectPeopleProperties(properties);
    }
    this.mixpanel.people.set(properties);
  }

  /**
   * Increments people properties the instance project
   */
  public incrementProjectPeopleProperties(properties) {
    this.mixpanel[this.appName].people.increment(properties);
  }

  /**
   * Increments people properties all mixpanel projects
   */
  public incrementPeopleProperties(properties) {
    if (this.isInstanceProject) {
      this.incrementProjectPeopleProperties(properties);
    }
    this.mixpanel.people.increment(properties);
  }

  public trackLinks(
    query: any,
    event: string,
    properties: object,
    options: TrackingOptions = {includeReportPrefix: true},
  ) {
    if (this.isInstanceProject) {
      this.mixpanel[this.appName].track_links(query, event, properties);
    }

    if (this.productionEvents.has(event)) {
      const eventName =
        this.productionPrefix && options.includeReportPrefix ? `[${this.productionPrefix}] ${event}` : event;
      this.mixpanel.track_links(query, eventName, properties);
    }
  }

  public identifyUser() {
    const user = this.getUserData();

    if (this.isInstanceProject) {
      this.mixpanel[this.appName].identify(user.userID);
      this.mixpanel[this.appName].people.set({
        $email: user.userEmail,
        $name: user.userName,
      });
    }

    return user;
  }

  public setupRollbar() {
    const user = this.getUserData();

    rollbar.configure({
      payload: {
        client: {
          javascript: {
            code_version: `1.0`, // eslint-disable-line camelcase
            guess_uncaught_frames: true, // eslint-disable-line camelcase
            source_map_enabled: true, // eslint-disable-line camelcase
          },
        },
        person: {
          email: user.userEmail,
          id: user.userID,
        },
      },
    });
  }

  public getUserData() {
    return pick(this.context, [`projectID`, `userEmail`, `userID`, `userName`]);
  }

  private populateEventProperties(properties: object, includeReportProperties = false) {
    const user = this.getUserData();

    return {
      ...(includeReportProperties ? this.appPersistentProperties() : {}),
      ...properties,
      Email: user.userEmail,
      project_id: user.projectID,
    };
  }
}
